import React, { useEffect, useState } from 'react';

import { Checkbox } from '@blueprintjs/core';
import { GridApi, GridOptions } from 'ag-grid-community';

import GridV2 from 'app/components/AdvancedGrid/GridV2';
import buildSellerSheetColumnDef from 'app/components/AdvancedGrid/Sheets/Seller/SellerSheetDetail/SellerSheetColumnDef';
import {
  buildSheetsPreviewBottomRowData,
  buildSheetsPreviewColumnData
} from 'app/components/DataPanel/SheetsPanel/SheetDetail/buildSheetsPreviewColumnsData';
import SheetDetailHeader from 'app/components/DataPanel/SheetsPanel/SheetDetailHeader/SheetDetailHeader';
import SheetGridHeader from 'app/components/DataPanel/SheetsPanel/SheetGridHeader/SheetGridHeader';
import SheetsAddFieldDrawer from 'app/components/SheetsAddFieldDrawer/SheetsAddFieldDrawer';

import { useData } from 'app/contexts/dataProvider';
import { useScope } from 'app/contexts/scopeProvider';

import usePhase from 'app/hooks/usePhase';

import { DataPanelViews, DeploymentModelPhase } from 'app/models';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import style from './SellerSheetDetail.module.pcss';

const b = block(style);

const SellerSheetDetail: React.FC = () => {
  const deploymentModelPhase = usePhase();
  const {
    dataSheetDrawerState,
    setSelectedSheet,
    selectedSheet,
    getSheetDefinitions,
    sheetDefinitions,
    setSelectedDataView,
    sheetDefinitionsLoading
  } = useData();
  const [sellerSheetShowTotals, setSellerSheetShowTotals] = useState<boolean>(true);

  const [sellerSheetGridApi, setSellerSheetGridApi] = useState<GridApi>(null);

  const onSellerSheetGridReady = (params) => {
    setSellerSheetGridApi(params?.api);
  };
  const { selectedDeploymentModelId } = useScope();
  const sellerSheetGridOptions: GridOptions = {
    getRowClass: (params) => {
      let className = '';
      if (params.node.rowPinned === 'bottom') {
        className = b('bottomRowPinned');
      }
      return className;
    },
    loading: sheetDefinitionsLoading,
    columnDefs: buildSellerSheetColumnDef(sheetDefinitions),
    rowData: buildSheetsPreviewColumnData(sheetDefinitions),
    onGridReady: onSellerSheetGridReady,
    pinnedBottomRowData: [buildSheetsPreviewBottomRowData(sheetDefinitions)]
  };

  useEffect(() => {
    if (!sellerSheetGridOptions || !sellerSheetGridApi) {
      return;
    }
    if (sellerSheetShowTotals) {
      sellerSheetGridApi.setGridOption('pinnedBottomRowData', [buildSheetsPreviewBottomRowData(sheetDefinitions)]);
    } else {
      sellerSheetGridApi.setGridOption('pinnedBottomRowData', []);
    }
  }, [sellerSheetShowTotals, sellerSheetGridApi]);

  useEffect(() => {
    const isTQM = deploymentModelPhase === DeploymentModelPhase.manage;
    if (selectedSheet) {
      getSheetDefinitions(selectedDeploymentModelId, selectedSheet.sheetId, isTQM);
    }
  }, [selectedSheet]);

  const onBackClick = () => {
    setSelectedDataView(DataPanelViews.DATA_OVERVIEW);
    setSelectedSheet(null);
  };

  return (
    <div className={b()} data-testid="seller-sheet-detail">
      <SheetDetailHeader onBackClick={onBackClick} />
      <div className={b('content')}>
        <div>
          <SheetGridHeader
            title={formatMessage('SELLER_SHEET_COLUMNS')}
            subtitle={formatMessage('COLUMNS_DESCRIPTION')}
          />
          <div className={b('sellerSheetColumnsGrid')}>
            <GridV2 {...sellerSheetGridOptions} data-testid="seller-sheet-columns-grid" />
          </div>
        </div>
        <div className={b('showTotalsArea')}>
          <Checkbox
            data-testid="show-totals-checkbox"
            checked={sellerSheetShowTotals}
            onClick={() => setSellerSheetShowTotals(!sellerSheetShowTotals)}
          />
          <span className={b('showTotalsText')} data-testid="show-totals-text">
            {formatMessage('SHOW_TOTALS')}
          </span>
        </div>
      </div>
      {dataSheetDrawerState && <SheetsAddFieldDrawer />}
    </div>
  );
};

export default SellerSheetDetail;
